// components/UI.jsx — Reusable design system components

import React from "react";

// ── Button ─────────────────────────────────────────────────────────────────
export const Button = React.forwardRef(
  ({ children, variant = "primary", size = "md", className = "", ...props }, ref) => {
    const base =
      "inline-flex items-center justify-center font-medium rounded-xl transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A3E635] focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed select-none";

    const variants = {
      primary:
        "bg-[#A3E635] text-[#0F172A] hover:bg-[#bef264] active:scale-95 shadow-lg shadow-[#A3E635]/20 font-bold",
      secondary:
        "border-2 border-current opacity-70 hover:opacity-100 hover:border-[#A3E635] hover:text-[#A3E635] active:scale-95",
      ghost: "opacity-60 hover:opacity-100 hover:text-[#A3E635] active:scale-95",
      danger:
        "bg-red-500 text-white hover:bg-red-600 active:scale-95 shadow-md",
      outline:
        "border border-[#E2E8F0]/50 hover:border-[#A3E635] hover:text-[#A3E635] active:scale-95",
    };

    const sizes = {
      sm: "px-3 py-1.5 text-xs",
      md: "px-5 py-2.5 text-sm",
      lg: "px-7 py-3.5 text-base",
      xl: "px-9 py-4 text-lg",
    };

    return (
      <button
        ref={ref}
        className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
        {...props}
      >
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";

// ── Card ─────────────────────────────────────────────────────────────────
export const Card = ({ children, className = "", hover = false, dark = false, onClick }) => (
  <div
    onClick={onClick}
    className={[
      "rounded-2xl border transition-all duration-300",
      dark ? "bg-white/5 border-white/10" : "bg-white border-[#E2E8F0]",
      hover
        ? "hover:border-[#A3E635]/60 hover:shadow-xl hover:shadow-[#A3E635]/5 hover:-translate-y-1 cursor-pointer"
        : "",
      className,
    ].join(" ")}
  >
    {children}
  </div>
);

// ── Input ─────────────────────────────────────────────────────────────────
export const Input = ({ className = "", dark = false, ...props }) => (
  <input
    className={[
      "w-full rounded-xl border px-4 py-3 text-sm transition-all duration-200 outline-none",
      "focus:border-[#A3E635] focus:ring-2 focus:ring-[#A3E635]/20",
      dark
        ? "bg-white/5 border-white/15 text-white placeholder:text-white/30"
        : "bg-white border-[#E2E8F0] text-[#0F172A] placeholder:text-[#0F172A]/30",
      className,
    ].join(" ")}
    {...props}
  />
);

// ── Textarea ─────────────────────────────────────────────────────────────────
export const Textarea = ({ className = "", dark = false, ...props }) => (
  <textarea
    className={[
      "w-full rounded-xl border px-4 py-3 text-sm transition-all duration-200 outline-none resize-none",
      "focus:border-[#A3E635] focus:ring-2 focus:ring-[#A3E635]/20",
      dark
        ? "bg-white/5 border-white/15 text-white placeholder:text-white/30"
        : "bg-white border-[#E2E8F0] text-[#0F172A] placeholder:text-[#0F172A]/30",
      className,
    ].join(" ")}
    {...props}
  />
);

// ── SectionWrapper ──────────────────────────────────────────────────────────
export const SectionWrapper = ({
  id,
  children,
  className = "",
  dark = false,
  bg = "default",
}) => {
  const bgClass =
    bg === "alt"
      ? dark
        ? "bg-white/[0.03]"
        : "bg-[#E2E8F0]/25"
      : dark
      ? "bg-[#0F172A]"
      : "bg-white";

  return (
    <section
      id={id}
      className={`py-24 px-4 sm:px-6 lg:px-8 ${bgClass} ${dark ? "text-white" : "text-[#0F172A]"} ${className}`}
    >
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
};

// ── SectionTitle ──────────────────────────────────────────────────────────
export const SectionTitle = ({ title, subtitle, center = true, dark = false }) => (
  <div className={`mb-14 ${center ? "text-center" : ""}`}>
    <h2 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight">{title}</h2>
    {subtitle && (
      <p
        className={`text-base sm:text-lg max-w-2xl ${center ? "mx-auto" : ""} ${
          dark ? "text-white/50" : "text-[#0F172A]/50"
        }`}
      >
        {subtitle}
      </p>
    )}
  </div>
);

// ── Badge ──────────────────────────────────────────────────────────────
export const Badge = ({ children, className = "" }) => (
  <span
    className={`inline-block text-xs font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full bg-[#A3E635]/10 text-[#A3E635] border border-[#A3E635]/20 ${className}`}
  >
    {children}
  </span>
);

// ── Modal ──────────────────────────────────────────────────────────────────
export const Modal = ({ open, onClose, children }) => {
  // Close on Escape key
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

// ── Divider ──────────────────────────────────────────────────────────────────
export const Divider = ({ dark }) => (
  <div className={`h-px w-full ${dark ? "bg-white/10" : "bg-[#E2E8F0]"}`} />
);

// ── Label (form) ──────────────────────────────────────────────────────────────
export const Label = ({ children, className = "", dark = false }) => (
  <label className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${dark ? "text-white/50" : "text-[#0F172A]/50"} ${className}`}>
    {children}
  </label>
);

// ── Toggle Switch ──────────────────────────────────────────────────────────────
export const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer select-none">
    <div
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
        checked ? "bg-[#A3E635]" : "bg-gray-400/30"
      }`}
    >
      <div
        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </div>
    {label && <span className="text-sm">{label}</span>}
  </label>
);
